import React, { useCallback, useEffect, useRef, useState } from 'react';
import DualBarGroupChart from '../components/DualBarGroupChart';
import { CalendarIcon } from '@heroicons/react/solid';

const EQUIPMENT_ROWS = [
  { key: 'feed_conveyor_belt_spray_water_nozzles', label: 'Feed conveyor belt spray water nozzles' },
  { key: 'deslimming_screen_panels_static_and_polywedge', label: 'Deslimming screen panels' },
  { key: 'drain_and_rinse_screen_panels_static_and_polywedge', label: 'Drain & rinse screen panels' },
  { key: 'drain_and_rinse_screen_spray_nozzles', label: 'Drain & rinse screen spray nozzles' },
  { key: 'dewatering_screen_panels_static_and_polywedge', label: 'Dewatering screen panels' },
  { key: 'magnetic_separator', label: 'Magnetic separator' },
  { key: 'densification_cyclone_spigot_diameter', label: 'Densification cyclone spigot diameter' },
  { key: 'spiral_product_cyclone_spigot_diameter', label: 'Spiral product cyclone spigot diameter' },
  { key: 'spiral_discard_cyclone_spigot_diameter', label: 'Spiral discard cyclone spigot diameter' },
  { key: 'dense_medium_cyclone_spigot_diameter', label: 'Dense medium cyclone spigot diameter' },
  { key: 'spiral_distribution_pipes', label: 'Spiral distribution pipes' },
  { key: 'cm_headerbox_pipes', label: 'CM headerbox pipes' },
];

const WEAR_CHART_ITEMS = [
  { key: 'dense_medium_cyclone_spigot_diameter', label: 'Dense medium cyclone spigot diameter', initial: 205 },
  { key: 'classification_cyclone_spigot_diameter', label: 'Classification cyclone spigot diameter', initial: 85 },
  { key: 'densification_cyclone_spigot_diameter', label: 'Densification cyclone spigot diameter', initial: 40 },
  { key: 'spiral_product_cyclone_spigot_diameter', label: 'Spiral product cyclone spigot diameter', initial: 40 },
  { key: 'spiral_discard_cyclone_spigot_diameter', label: 'Spiral discard cyclone spigot diameter', initial: 30 },
  { key: 'cm_headerbox_orifices_make_up_medium', label: 'Cm headerbox orifices make up medium', initial: 130 },
  { key: 'cm_headerbox_orifices_push_medium', label: 'Cm headerbox orifices push medium', initial: 140 },
  { key: 'thickener_underflow_density', label: 'Thickener underflow density', initial: 0 },
];

const hasWearData = record => {
  if (!record) return false;
  return WEAR_CHART_ITEMS.some(item => {
    const rawValue = record[`${item.key}_wear_value`];
    if (rawValue === null || rawValue === undefined) {
      return false;
    }
    const numericValue = parseFloat(rawValue);
    return !Number.isNaN(numericValue) && numericValue !== 0;
  });
};

function ProcessEquipmentChecks() {
  const [recordState, setRecordState] = useState({ record: null, hasMore: false });
  const [currentOffset, setCurrentOffset] = useState(0);
  const [rangeLabel, setRangeLabel] = useState('');

  const requestIdRef = useRef(0);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchRecordByOffset = useCallback(async offset => {
    try {
      const response = await fetch(`/api/process-equipment-checks.php?offset=${offset}`);
      const data = await response.json();
      if (data && typeof data === 'object' && 'record' in data) {
        const record = data.record && Object.keys(data.record).length > 0 ? data.record : null;
        return { record, hasMore: Boolean(data.hasMore) };
      }
      const fallbackRecord = data && typeof data === 'object' && Object.keys(data).length ? data : null;
      return { record: fallbackRecord, hasMore: false };
    } catch (error) {
      return { record: null, hasMore: false };
    }
  }, []);

  const navigateToOffset = useCallback(
    async (startOffset, step, { allowEmpty = false } = {}) => {
      if (startOffset < 0) {
        if (allowEmpty) {
          setCurrentOffset(0);
          setRecordState({ record: null, hasMore: false });
        }
        return false;
      }

      const requestId = ++requestIdRef.current;
      let offset = startOffset;
      let lastHasMore = false;
      let lastCheckedOffset = startOffset;

      while (offset >= 0) {
        const result = await fetchRecordByOffset(offset);
        if (!isMountedRef.current || requestId !== requestIdRef.current) {
          return false;
        }

        lastHasMore = result.hasMore;
        lastCheckedOffset = offset;

        if (result.record && hasWearData(result.record)) {
          setCurrentOffset(offset);
          setRecordState({ record: result.record, hasMore: result.hasMore });
          return true;
        }

        if (step > 0) {
          if (!result.hasMore) {
            break;
          }
          offset += step;
        } else if (step < 0) {
          if (offset === 0) {
            break;
          }
          offset += step;
        } else {
          break;
        }
      }

      if (!isMountedRef.current || requestId !== requestIdRef.current) {
        return false;
      }

      if (allowEmpty) {
        setCurrentOffset(Math.max(0, lastCheckedOffset));
        setRecordState({ record: null, hasMore: lastHasMore });
      } else {
        setRecordState(prev => ({ ...prev, hasMore: lastHasMore }));
      }

      return false;
    },
    [fetchRecordByOffset]
  );

  useEffect(() => {
    navigateToOffset(0, 1, { allowEmpty: true });
  }, [navigateToOffset]);

  useEffect(() => {
    if (recordState.record && recordState.record.timestamp) {
      const label = new Date(recordState.record.timestamp).toLocaleString('en-GB');
      setRangeLabel(label);
    } else {
      setRangeLabel('');
    }
  }, [recordState.record]);

  const [rowData, setRowData] = useState(() =>
    EQUIPMENT_ROWS.reduce((acc, r) => {
      acc[r.key] = { index: 0, record: null, hasMore: false };
      return acc;
    }, {})
  );

  const fetchRow = (key, index) => {
    fetch(`/api/process-equipment-checks.php?equipment=${key}&offset=${index}`)
      .then(res => res.json())
      .then(res => {
        setRowData(prev => ({
          ...prev,
          [key]: { index, record: res.record, hasMore: res.hasMore },
        }));
      })
      .catch(() => {
        setRowData(prev => ({ ...prev, [key]: { index, record: null, hasMore: false } }));
      });
  };

  useEffect(() => {
    EQUIPMENT_ROWS.forEach(r => fetchRow(r.key, 0));
  }, []);

  const shiftRow = (key, delta) => {
    const current = rowData[key];
    const newIndex = current.index + delta;
    if (newIndex < 0) return;
    fetchRow(key, newIndex);
  };

  const record = recordState.record;

  const chartData = WEAR_CHART_ITEMS.map(item => ({
    label: item.label,
    initial: item.initial,
    current: record ? parseFloat(record[`${item.key}_wear_value`]) || 0 : 0,
  }));

  const prevRecord = () => {
    navigateToOffset(currentOffset + 1, 1);
  };

  const nextRecord = () => {
    if (currentOffset === 0) {
      return;
    }
    navigateToOffset(currentOffset - 1, -1);
  };

  const disableNext = currentOffset === 0;
  const disablePrev = !recordState.hasMore;

  const renderRow = r => {
    const state = rowData[r.key] || {};
    const record = state.record || {};
    const value = (record.value || '').toLowerCase();
    const comment = record.comment || '';
    const timestamp = record.timestamp ? new Date(record.timestamp).toLocaleString('en-GB') : '-';
    const isOnline = value === 'online';
    const isOffline = value === 'offline';
    return (
      <tr key={r.key} className="even:bg-[#0f3346]">
        <td className="px-1 py-0.5 text-sm text-center w-1/5">
          <div className="flex items-center justify-center space-x-1">
            <button
              onClick={() => shiftRow(r.key, 1)}
              disabled={!state.hasMore}
              className="px-1 py-0.5 bg-[#0d2c3d] rounded hover:bg-[#123b52] disabled:opacity-50"
              aria-label="Previous Record"
            >
              {'<'}
            </button>
            <span>{timestamp}</span>
            <button
              onClick={() => shiftRow(r.key, -1)}
              disabled={state.index === 0}
              className="px-1 py-0.5 bg-[#0d2c3d] rounded hover:bg-[#123b52] disabled:opacity-50"
              aria-label="Next Record"
            >
              {'>'}
            </button>
          </div>
        </td>
        <td className="px-1 py-0.5 text-sm text-center w-1/5">{r.label}</td>
        <td className="px-1 py-0.5 text-sm text-center w-1/5">
          <span className={`inline-block w-5 h-5 rounded-full ${isOnline ? 'bg-green-500' : 'bg-green-900 opacity-30'}`}></span>
        </td>
        <td className="px-1 py-0.5 text-sm text-center w-1/5">
          <span className={`inline-block w-5 h-5 rounded-full ${isOffline ? 'bg-red-500' : 'bg-red-900 opacity-30'}`}></span>
        </td>
        <td className="px-1 py-0.5 text-sm text-center w-1/5 break-words">{comment}</td>
      </tr>
    );
  };

  return (
    <div className="p-4 space-y-4 text-white flex h-full flex-col">
      <div className="flex items-center justify-center border-b border-gray-600 pb-2">
        <div className="flex items-center space-x-2">
          <button
            onClick={prevRecord}
            disabled={disablePrev}
            className="px-2 py-1 bg-[#0d2c3d] rounded shadow hover:bg-[#123b52] disabled:opacity-50 transition ease-in-out duration-200"
            aria-label="Previous Record"
          >
            {'<'}
          </button>
          <div
            className="px-3 py-1 bg-[#0d2c3d] rounded shadow flex items-center space-x-1 transition transform hover:scale-105"
            title="Record view – click arrows to navigate"
          >
            <CalendarIcon className="h-4 w-4" />
            <span className="text-sm">{rangeLabel || 'No record available'}</span>
          </div>
          <button
            onClick={nextRecord}
            disabled={disableNext}
            className="px-2 py-1 bg-[#0d2c3d] rounded shadow hover:bg-[#123b52] disabled:opacity-50 transition ease-in-out duration-200"
            aria-label="Next Record"
          >
            {'>'}
          </button>
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-4 min-h-0">
        <div className="bg-[#0d2c3d] shadow-lg rounded-lg p-4 flex flex-col overflow-hidden flex-[7] min-h-0">
          <h2 className="text-center text-sm mb-2 flex-shrink-0">Process Equipment Measurements: Initial Wear vs Current Wear</h2>
          <div className="flex-1 relative min-h-0">
            <DualBarGroupChart data={chartData} />
          </div>
        </div>

        <div className="bg-[#0d2c3d] shadow-lg rounded-lg p-4 flex flex-col overflow-hidden flex-[9] min-h-0">
          <h2 className="text-center text-sm mb-2 flex-shrink-0">Process Equipment Inspections</h2>
          <div className="flex-1 overflow-auto min-h-0">
            <table className="w-full text-sm table-fixed rounded-lg overflow-hidden">
              <thead>
                <tr className="bg-green-700">
                  <th className="px-1 py-0.5 text-sm text-center w-1/5">Date &amp; Time</th>
                  <th className="px-1 py-0.5 text-sm text-center w-1/5">Equipment</th>
                  <th className="px-1 py-0.5 text-sm text-center w-1/5">Online</th>
                  <th className="px-1 py-0.5 text-sm text-center w-1/5">Offline</th>
                  <th className="px-1 py-0.5 text-sm text-center w-1/5">Comment</th>
                </tr>
              </thead>
              <tbody className="text-center">
                {EQUIPMENT_ROWS.map(renderRow)}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProcessEquipmentChecks;
